import React, { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useLocation } from 'umi';
import { Layout } from 'tea-component';
import { buriedPointGame, typeIdObj } from '@/pages/home/utils/buriedPoint';
import { StudioContext } from '@home/pages/studio/context';
import StudioQueryTabs from '../StudioQueryTabs';
import MarketingPageWrap from '../MarketingPageWrap';
import StudioTabPrNoticeMarquee from '../StudioTabPrNoticeMarquee';
import ZoneTrendingPublicationsAdapter from '../ZoneTrendingPublicationsAdapter';
import type {
  ITrendingPostsVdieoTabConfig,
  ITrendingPostsVdieoWrapperConfig,
  IZoneTrendingPublicationsAdapterStudioProps,
} from './types';
import './index.scss';

const { Content } = Layout;

const tabLogDedupe = { key: '', at: 0 };
const TAB_LOG_DEDUPE_MS = 800;

function reportStudioTabLog(
  tab: ITrendingPostsVdieoTabConfig | undefined,
  gameName: string | undefined,
  gameId: string | undefined,
) {
  const logConfig = tab?.logConfig;
  const tabKey = tab?.key;
  if (!logConfig?.enabled || !gameId || !tabKey) {
    return;
  }
  const now = Date.now();
  if (tabLogDedupe.key === tabKey && now - tabLogDedupe.at < TAB_LOG_DEDUPE_MS) {
    return;
  }
  tabLogDedupe.key = tabKey;
  tabLogDedupe.at = now;
  buriedPointGame({
    gameName,
    pageId: logConfig.pageId || '',
    pageName: logConfig.pageName,
    extInfo: logConfig.extInfo,
    pagePath: logConfig.pagePath,
    typeId: typeIdObj.studio,
  }, gameId, 'unified_id');
}

function resolveActiveTabKey(
  tabs: ITrendingPostsVdieoTabConfig[],
  queryValue: string | null,
  defaultTab?: string,
) {
  const validKeys = new Set(tabs.map((tab) => tab.key));
  if (queryValue && validKeys.has(queryValue)) {
    return queryValue;
  }
  if (defaultTab && validKeys.has(defaultTab)) {
    return defaultTab;
  }
  return tabs[0]?.key;
}

function resolveCondGameIds(
  tab: ITrendingPostsVdieoTabConfig,
  wrapperCondGameIds?: string[],
) {
  const tabCondGameIds = tab.componentProps?.condGameIds;
  if (Array.isArray(tabCondGameIds)) {
    return tabCondGameIds;
  }
  return wrapperCondGameIds;
}

interface ITrendingPostsVdieoWrapperProps {
  config?: ITrendingPostsVdieoWrapperConfig;
}

const TrendingPostsVdieoWrapper: React.FC<ITrendingPostsVdieoWrapperProps> = ({ config }) => {
  const location = useLocation();
  const { currentGame } = useContext(StudioContext);
  const { unified_edition_id: gameId } = currentGame || {};
  const {
    tabs = [],
    queryKey = 'tab',
    defaultTab,
    titleEn,
    titleCn,
    descriptionEn,
    descriptionCn,
    condGameIds: wrapperCondGameIds,
  } = config || {};

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const activeKey = resolveActiveTabKey(
    tabs,
    searchParams.get(queryKey),
    defaultTab,
  );
  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0];
  const adapterProps = useMemo((): IZoneTrendingPublicationsAdapterStudioProps | undefined => {
    if (!activeTab) {
      return undefined;
    }
    const props = (activeTab.componentProps || {}) as IZoneTrendingPublicationsAdapterStudioProps;
    return {
      ...props,
      condGameIds: resolveCondGameIds(activeTab, wrapperCondGameIds),
    };
  }, [activeTab, wrapperCondGameIds]);
  const isMultiTab = tabs.length > 1;
  const hasPageTitle = !!(titleEn || titleCn);
  const showHeader = isMultiTab || hasPageTitle;

  useEffect(() => {
    if (!isMultiTab || !gameId || !activeTab) {
      return;
    }
    reportStudioTabLog(activeTab, currentGame?.game_name, gameId);
  }, [activeKey, gameId, isMultiTab, activeTab, currentGame?.game_name]);

  return (
    <Content className={classNames('dbh-studio-trending-posts-vdieo', hasPageTitle && 'dbi-businessreports')}>
      {showHeader && (
        <StudioQueryTabs
          titleEn={titleEn}
          titleCn={titleCn}
          descriptionEn={descriptionEn}
          descriptionCn={descriptionCn}
          tabs={isMultiTab ? tabs.map((tab) => ({
            key: tab.key,
            labelEn: tab.nameEn,
            labelCn: tab.nameCn,
          })) : []}
          activeKey={activeKey}
          defaultActiveKey={defaultTab}
          queryKey={queryKey}
        />
      )}
      <Content.Body full className="dbh-studio-trending-posts-vdieo-body">
        {adapterProps && (
          <MarketingPageWrap>
            <StudioTabPrNoticeMarquee
              tabKey={activeTab?.key}
              prNoticeMarquee={activeTab?.prNoticeMarquee}
            />
            <ZoneTrendingPublicationsAdapter {...adapterProps} />
          </MarketingPageWrap>
        )}
      </Content.Body>
    </Content>
  );
};

export default TrendingPostsVdieoWrapper;
